<template>
  <FairSurveyDialog
    v-model:open="visible"
    :title="surveyTitle"
    :show-title="isWelcomeStep"
    :exit-label="isWelcomeStep ? 'Назад' : 'Сохранить и выйти'"
    :show-progress="showProgress"
    :progress-percent="progressPercentage"
    :progress-label="progressLabel"
    :loading="surveyModal.isLoadingConfig"
    loading-label="Загрузка опроса…"
    :show-footer="showFooter"
    :show-saved="showSavedIndicator"
    saved-label="Данные сохранены"
    :compact="!isDesktop"
    @close="handleHideSurveyModal"
  >
    <DynamicStepRenderer />

    <template #actions>
      <FairButton
        v-if="showBackButton"
        label="Назад"
        variant="tonal"
        size="m"
        :rounded="false"
        @click="prevStep"
      />
      <FairButton
        v-if="showNextButton"
        :label="nextButtonLabel"
        variant="primary"
        size="m"
        :rounded="false"
        :disabled="!surveyStore.validateCurrentStep() || surveyModal.isLoading"
        @click="handleNext"
      />
    </template>
  </FairSurveyDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { FairSurveyDialog } from "@/ui/patterns/FairSurveyDialog";
import { FairButton } from "@/ui/primitives/FairButton";

import { useSurveyModal } from "@/features/survey/store";
import DynamicStepRenderer from "./SurveySteps/DynamicStepRenderer.vue";
import { useDevice } from "@/composables/useDevice";

const surveyStore = useSurveyModal();
const { isDevice } = useDevice()

const isWelcomeStep = computed(() => {
  return currentStep.value?.type === "welcome";
});
const surveyModal = computed(() => surveyStore.surveyModal);
const currentStep = computed(() => surveyStore.currentStep);
const totalSteps = computed(() => surveyStore.totalSteps);

const deviceType = computed(() => isDevice.value)
const isDesktop = computed(() => deviceType.value === 'desktop' || deviceType.value === 'smallDesktop')

const visible = ref(false);
const savedSteps = ref<Set<string>>(new Set());

const surveyTitle = computed(
  () => surveyStore.surveyModal.config?.title || "Мои размеры"
);

const progressPercentage = computed(() => {
  if (totalSteps.value === 0) return 0;
  return ((surveyModal.value.currentStepIndex + 1) / totalSteps.value) * 100;
});

/** Welcome has nothing to measure yet; thank-you is past the last step. */
const showProgress = computed(() => {
  const stepType = currentStep.value?.type;
  return Boolean(stepType) && stepType !== "welcome" && stepType !== "thank_you";
});

const progressLabel = computed(
  () => `${surveyModal.value.currentStepIndex + 1} из ${totalSteps.value}`
);

const showBackButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const isFirstStep = surveyModal.value.currentStepIndex === 0;
  
  // Hide back button on special steps
  if (stepType === 'choice' || stepType === 'completion') {
    return false;
  }
  
  return !isFirstStep;
});

const showNextButton = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const isLastStep = surveyModal.value.currentStepIndex >= totalSteps.value - 1;
  
  // Hide next button on special steps
  if (stepType === 'choice' || stepType === 'thank_you' || stepType === 'completion') {
    return false;
  }
  
  return !isLastStep;
});

const nextButtonLabel = computed(() => {
  if (!currentStep.value) return "Продолжить";
  
  if (currentStep.value.type === 'welcome') return "Начать тест";
  if (currentStep.value.type === 'thank_you') return "Посмотреть результаты";
  
  return "Продолжить";
});

const showFooter = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  
  // Hide footer on special steps
  return stepType !== 'choice' && stepType !== 'thank_you' && stepType !== 'completion';
});

const showSavedIndicator = computed(() => {
  if (!currentStep.value) return false;
  const stepType = currentStep.value.type;
  const stepId = currentStep.value.id;

  // Не показываем плашку для шагов без данных или без футера
  if (stepType === 'welcome' || stepType === 'thank_you' || stepType === 'completion' || stepType === 'choice') {
    return false;
  }

  const stepValue = surveyStore.surveyModal.surveyData[stepId];
  const hasData =
    stepValue !== undefined &&
    stepValue !== null &&
    (Array.isArray(stepValue) ? stepValue.length > 0 : stepValue !== '');

  return hasData && savedSteps.value.has(stepId);
});

function handleHideSurveyModal() {
  surveyStore.hideSurveyModal();
}

function prevStep() {
  surveyStore.prevStep();
}

function handleNext() {
  if (!currentStep.value) return;
  
  if (currentStep.value.type === 'thank_you') {
    // Submit survey and go to results
    surveyStore.submitSurvey();
  } else {
    surveyStore.nextStep();
  }
}

watch(
  () => surveyModal.value.isShow,
  (newVal) => {
    visible.value = newVal;
    if (!newVal) {
      savedSteps.value.clear();
    }
  }
);

// Отслеживаем изменения данных текущего шага и помечаем его как сохраненный
watch(
  () => surveyStore.surveyModal.surveyData,
  (newData) => {
    if (!currentStep.value) return;
    const stepId = currentStep.value.id;
    const stepType = currentStep.value.type;

    if (stepType === 'welcome' || stepType === 'thank_you' || stepType === 'completion' || stepType === 'choice') {
      return;
    }

    const stepValue = newData[stepId];
    const hasData =
      stepValue !== undefined &&
      stepValue !== null &&
      (Array.isArray(stepValue) ? stepValue.length > 0 : stepValue !== '');

    if (hasData) {
      savedSteps.value.add(stepId);
    }
  },
  { deep: true }
);

// При смене шага проверяем, есть ли уже данные, чтобы показать плашку сразу
watch(
  () => currentStep.value?.id,
  (newStepId) => {
    if (!newStepId || !currentStep.value) return;
    const stepType = currentStep.value.type;

    if (stepType === 'welcome' || stepType === 'thank_you' || stepType === 'completion' || stepType === 'choice') {
      return;
    }

    const stepValue = surveyStore.surveyModal.surveyData[newStepId];
    const hasData =
      stepValue !== undefined &&
      stepValue !== null &&
      (Array.isArray(stepValue) ? stepValue.length > 0 : stepValue !== '');

    if (hasData) {
      savedSteps.value.add(newStepId);
    }
  }
);
</script>
